/**
 * Custom Order Utilities
 * Centralized utility functions for custom order operations
 * @namespace CustomOrderUtils
 */

class CustomOrderUtils {
  /**
   * Determines if a cart item is a custom order
   * Products using the custom-by-type / custom-measure template are custom orders;
   * they are identified by line item properties (measurement form adds _custom, Order Type, or measurement keys).
   * @param {Object} item - Cart item object
   * @returns {boolean} - True if item is a custom order
   */
  static isCustomOrderItem(item) {
    if (!item) return false;

    if (item.properties) {
      const customFlag = item.properties['_custom'] || item.properties['Order Type'];
      if (typeof customFlag === 'string') {
        const flagLower = customFlag.toLowerCase();
        if (flagLower === 'custom' || flagLower.includes('custom')) {
          return true;
        }
      }

      for (const [propertyName] of Object.entries(item.properties)) {
        const nameLower = String(propertyName).toLowerCase();
        if (
          nameLower.includes('(in)') ||
          nameLower.includes('(cm)') ||
          nameLower.includes('unit of measure') ||
          nameLower.includes('selected option')
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Determines if a property should be filtered out
   * Filters empty, zero, and internal (underscore-prefixed) properties
   * Also filters properties with "- Text" suffix that are empty
   * @param {string} propertyName - Property name
   * @param {string} propertyValue - Property value
   * @returns {boolean} - True if property should be filtered
   */
  static shouldFilterProperty(propertyName, propertyValue) {
    if (!propertyName) return false;

    // Filter internal properties (starting with underscore)
    if (propertyName.charAt(0) === '_') {
      return true;
    }

    // Convert value to string and trim
    const stringValue = propertyValue != null ? String(propertyValue) : '';
    const trimmedValue = stringValue.trim();

    // Filter empty/blank values (including null, undefined, empty string, whitespace-only)
    if (trimmedValue === '') {
      return true;
    }

    // Filter properties with "- Text" or " - Text" suffix that are empty
    // These are generated by Shopify for "Other" option text inputs
    if ((propertyName.endsWith('- Text') || propertyName.endsWith(' - Text')) && trimmedValue === '') {
      return true;
    }

    // Filter zero values (0, 0.0, 0.00, 0.000, etc.)
    const numValue = parseFloat(trimmedValue);

    if (!isNaN(numValue) && numValue === 0) {
      // Check if the string representation is just zeros (with optional decimal point)
      const zeroPattern = /^0+(\.0+)?$/;
      if (zeroPattern.test(trimmedValue)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Filters cart item properties, removing empty, zero, and internal properties
   * @param {Object} properties - Cart item properties object
   * @returns {Object} - Filtered properties object
   */
  static filterCartItemProperties(properties) {
    if (!properties || typeof properties !== 'object') {
      return {};
    }

    const filtered = {};

    for (const [key, value] of Object.entries(properties)) {
      if (!this.shouldFilterProperty(key, value)) {
        filtered[key] = value;
      }
    }

    return filtered;
  }

  /**
   * Builds product page URL with query parameters for editing
   * @param {string} productHandle - Product handle
   * @param {Object} properties - Cart item properties
   * @param {number|string} variantId - Variant ID (optional)
   * @returns {string} - Product page URL with query parameters
   */
  /**
   * Derives effective product type from properties for type param in edit URLs.
   * Uses Product Type (with Harness Type fallback for backward compatibility).
   * @param {Object} properties - Cart item properties (filtered)
   * @returns {string|null} - Product type or null
   */
  static getEffectiveTypeFromProperties(properties) {
    if (!properties || !window.customMeasurementsConfig) return null;
    const config = window.customMeasurementsConfig;
    const productTypeMap = config.productTypeMap;
    const productTypeToHarnessType = config.productTypeToHarnessType;
    if (!productTypeMap) return null;

    const productTypeValue =
      properties['Product Type'] != null && String(properties['Product Type']).trim() !== ''
        ? String(properties['Product Type']).trim()
        : properties['Harness Type'] != null && String(properties['Harness Type']).trim() !== ''
          ? String(properties['Harness Type']).trim()
          : null;
    if (!productTypeValue) return null;

    // If Product Type value is itself a key in productTypeMap (e.g. by-type: "hat", "Leather Armband"), return it
    const productTypeValueLower = productTypeValue.toLowerCase();
    for (const type of Object.keys(productTypeMap)) {
      if (type.toLowerCase() === productTypeValueLower) return type;
    }

    // Otherwise resolve from category + sub-type (associate: Product Type = harness/tag type value)
    if (!productTypeToHarnessType) return null;
    const category = properties['Selected Option'];
    if (!category) return null;

    for (const type of Object.keys(productTypeMap)) {
      if (
        productTypeMap[type] === category &&
        productTypeToHarnessType[type] &&
        productTypeToHarnessType[type].trim().toLowerCase() === productTypeValueLower
      ) {
        return type;
      }
    }
    return null;
  }

  /**
   * @param {string} productHandle - Product handle
   * @param {Object} properties - Cart item properties
   * @param {number|string|null} variantId - Variant ID (optional)
   * @param {string|null} [productType] - Product type from item.product.type (optional; used when properties don't resolve)
   * @returns {string} - Product page URL with query parameters
   */
  static buildEditUrl(productHandle, properties, variantId = null, productType = null) {
    if (!productHandle) return '/';

    const baseUrl = `/products/${productHandle}`;
    const filteredProperties = this.filterCartItemProperties(properties || {});

    // Build a single param object so we can send it via URL or sessionStorage when URL would be too long
    const paramObj = {};

    if (variantId) {
      paramObj.variant = String(variantId);
    }

    const effectiveType =
      this.getEffectiveTypeFromProperties(filteredProperties) ||
      (productType != null && String(productType).trim() !== '' ? String(productType).trim() : null);
    const productTypeValue =
      filteredProperties['Product Type'] != null && String(filteredProperties['Product Type']).trim() !== ''
        ? String(filteredProperties['Product Type']).trim()
        : effectiveType;
    if (effectiveType) {
      paramObj.type = effectiveType;
    }
    if (productTypeValue) {
      paramObj['Sub type'] = productTypeValue;
      if (!(filteredProperties['Product Type'] != null && String(filteredProperties['Product Type']).trim() !== '')) {
        paramObj['Product Type'] = productTypeValue;
      }
    }

    for (const [key, value] of Object.entries(filteredProperties)) {
      if (key.charAt(0) === '_') continue;
      paramObj[key] = String(value);
    }

    const params = new URLSearchParams(paramObj);
    const queryString = params.toString();
    const fullUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    // Avoid URL length limits (~2048 in many browsers); store full state in sessionStorage and use short ref
    const maxUrlLength = 2000;
    if (fullUrl.length > maxUrlLength) {
      const editRef = Date.now().toString(36) + Math.random().toString(36).slice(2, 9);
      const storageKey = 'customOrderEdit_' + editRef;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(paramObj));
        const shortQuery = new URLSearchParams({ edit_ref: editRef });
        if (variantId) shortQuery.set('variant', String(variantId));
        return `${baseUrl}?${shortQuery.toString()}`;
      } catch (e) {
        return fullUrl;
      }
    }

    return fullUrl;
  }

  /**
   * Parses URL query parameters for form pre-filling.
   * If edit_ref is present, reads full state from sessionStorage (used when edit URL would exceed length limits).
   * @returns {Object} - Parsed property values
   */
  static parseUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const editRef = params.get('edit_ref');
    if (editRef) {
      try {
        const storageKey = 'customOrderEdit_' + editRef;
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          sessionStorage.removeItem(storageKey);
          return parsed;
        }
      } catch (e) {
        // Fall through to URL parsing
      }
    }

    const properties = {};
    for (const [key, value] of params.entries()) {
      const decodedKey = decodeURIComponent(key);
      const decodedValue = decodeURIComponent(value);
      properties[decodedKey] = decodedValue;
    }
    return properties;
  }

  /**
   * Waits for customMeasurementsConfig to be available
   * @param {number} timeout - Maximum time to wait in milliseconds (default: 2000)
   * @returns {Promise<Object|null>} - Config object or null if timeout
   */
  static async waitForConfig(timeout = 2000) {
    // If config is already available, return it immediately
    if (window.customMeasurementsConfig) {
      return window.customMeasurementsConfig;
    }

    // Wait for config to be loaded
    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = 50; // Check every 50ms

      const checkConfig = () => {
        if (window.customMeasurementsConfig) {
          resolve(window.customMeasurementsConfig);
          return;
        }

        const elapsed = Date.now() - startTime;
        if (elapsed >= timeout) {
          // Timeout reached, resolve with null
          resolve(null);
          return;
        }

        // Check again after interval
        setTimeout(checkConfig, checkInterval);
      };

      checkConfig();
    });
  }

  /**
   * Checks if URL parameters contain edit-related parameters
   * Used to distinguish edit mode from marketing/tracking parameters
   * Derives parameter detection from theme settings config
   * Waits for config to be available if needed
   * @returns {Promise<boolean>} - True if URL contains edit-related parameters
   */
  static async hasEditRelatedParams() {
    const urlParams = this.parseUrlParams();
    if (!urlParams || Object.keys(urlParams).length === 0) {
      return false;
    }

    // Wait for config to be available (with timeout)
    const config = await this.waitForConfig(2000);
    if (!config) {
      // Fallback: if config not available after timeout, use pattern matching only
      return this.hasEditRelatedParamsByPattern(urlParams);
    }

    // Build set of all valid option values from config
    const validOptionValues = new Set();

    // Add categories (for "Selected Option" param)
    if (config.categories && Array.isArray(config.categories)) {
      config.categories.forEach((cat) => validOptionValues.add(String(cat).trim()));
    }

    // Add all option values from config.options
    if (config.options) {
      const optionArrays = [
        config.options.associates,
        config.options.leatherColors,
        config.options.harnessTypes,
        config.options.tagOptions,
        config.options.frontPlates,
        config.options.backPlates,
        config.options.longSliders,
      ];

      optionArrays.forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((val) => validOptionValues.add(String(val).trim()));
        }
      });
    }

    // Build set of measurement names from config
    const measurementNames = new Set();
    if (config.measurements) {
      Object.keys(config.measurements).forEach((name) => measurementNames.add(String(name).trim()));
    }

    // Check if any URL param value or name matches config data
    for (const [paramName, paramValue] of Object.entries(urlParams)) {
      const value = String(paramValue).trim();
      const name = String(paramName).trim();

      // Check if param value exactly matches any config option value
      if (validOptionValues.has(value)) {
        return true;
      }

      // Check if param name exactly matches a measurement name from config
      if (measurementNames.has(name)) {
        return true;
      }

      // Check if param name contains any measurement name from config as a substring
      // This handles cases where measurement name appears in the param name
      // (e.g., "Neck (in)" contains "Neck" from config)
      for (const measName of measurementNames) {
        if (name.includes(measName)) {
          return true;
        }
      }

      // Check if any word in param name matches a measurement name
      // Split param name by common delimiters and check each part
      const nameParts = name.split(/[\s\(\)\[\]_-]+/);
      for (const part of nameParts) {
        const trimmedPart = part.trim();
        if (trimmedPart && measurementNames.has(trimmedPart)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Fallback method: checks for edit-related parameters by pattern matching
   * Used when config is not available
   * Without config, we cannot reliably detect edit params
   * Return false to avoid false positives from marketing params
   * @param {Object} urlParams - Parsed URL parameters
   * @returns {boolean} - Always returns false (requires config for accurate detection)
   */
  static hasEditRelatedParamsByPattern(urlParams) {
    // Without config, we cannot determine which params are edit-related
    // Return false to avoid false positives from marketing/tracking parameters
    return false;
  }

  /**
   * Determines if a FormData object represents a custom order
   * Custom order forms add properties[_custom] or properties[Order Type] or measurement keys.
   * @param {FormData} formData - Form data object
   * @param {string} _productTitle - Unused (kept for API compatibility)
   * @returns {boolean} - True if form data represents a custom order
   */
  static isCustomOrderFromFormData(formData, _productTitle) {
    if (!formData) return false;

    const orderType = formData.get('properties[Order Type]');
    const customFlag = formData.get('properties[_custom]');

    if (
      (typeof orderType === 'string' && orderType.toLowerCase().includes('custom')) ||
      (typeof customFlag === 'string' && customFlag.toLowerCase() === 'custom')
    ) {
      return true;
    }

    return false;
  }
}

// Export to window namespace (following theme pattern)
window.CustomOrderUtils = CustomOrderUtils;
